import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiDelete, apiFileUrl, apiGet, apiPatch, apiUpload } from '../../lib/api';

/**
 * Setările unei firme, dinspre client.
 *
 * Schema e declarată aici, nu importată din `@pcrm/be/settings`: `apps/crm` e `scope:fe` și
 * n-are voie să depindă de backend — aceeași alegere ca la `settings.api.ts`.
 */
const companySettingSchema = z.object({
  name: z.string(),
  /** `text` sau `file`; un tip necunoscut cade pe randarea ca text, ca la setările globale. */
  type: z.string(),
  /** Valoarea proprie a firmei. `null` = n-a scris nimic, sau a golit-o dinadins. */
  value: z.string().nullable(),
  /** `true` = firma n-are rând propriu, deci citește valoarea globală. */
  inherited: z.boolean(),
  globalValue: z.string().nullable(),
});

export type CompanySetting = z.infer<typeof companySettingSchema>;

export function isFileSetting(setting: CompanySetting): boolean {
  return setting.type === 'file';
}

/**
 * Ce se tipărește efectiv pentru firma asta.
 *
 * ⚠️ **Moștenirea se citește din `inherited`, nu din `value === null`.** O firmă își poate
 * goli dinadins ștampila, și atunci nu moștenește nimic — contractul ei iese cu linie de
 * semnătură. Confundate, ecranul ar arăta ștampila grupului pe o firmă care a cerut să n-o
 * aibă.
 */
export function effectiveValue(setting: CompanySetting): string | null {
  return setting.inherited ? setting.globalValue : setting.value;
}

export const companySettingsQueryKey = (companyId: string) =>
  ['company-settings', companyId] as const;

export const companySettingsQueryOptions = (companyId: string) =>
  queryOptions({
    queryKey: companySettingsQueryKey(companyId),
    queryFn: () =>
      apiGet(
        `/companies/${companyId}/settings`,
        z.object({ settings: z.array(companySettingSchema) }),
      ).then((body) => body.settings),
  });

export function updateCompanySetting(input: {
  companyId: string;
  name: string;
  value: string;
}): Promise<CompanySetting> {
  return apiPatch(
    `/companies/${input.companyId}/settings/${encodeURIComponent(input.name)}`,
    { value: input.value },
    z.object({ setting: companySettingSchema }),
  ).then((body) => body.setting);
}

/**
 * Revenirea la global.
 *
 * `DELETE`, nu un `PATCH` cu valoare goală: golul înseamnă „firma asta n-are ștampilă", nu
 * „citește-o pe a grupului".
 */
export function resetCompanySetting(input: { companyId: string; name: string }): Promise<void> {
  return apiDelete(`/companies/${input.companyId}/settings/${encodeURIComponent(input.name)}`);
}

export function uploadCompanySettingFile(input: {
  companyId: string;
  name: string;
  file: File;
}): Promise<CompanySetting> {
  return apiUpload(
    `/companies/${input.companyId}/settings/${encodeURIComponent(input.name)}/file`,
    input.file,
    z.object({ setting: companySettingSchema }),
  ).then((body) => body.setting);
}

/**
 * Adresa fișierului firmei, pentru `<img src>`.
 *
 * `v=` e cheia din storage, care conține hash-ul conținutului: o ștampilă înlocuită are
 * altă cheie, deci altă adresă, deci browserul chiar reîncarcă imaginea.
 *
 * ⚠️ **Numai pentru valoarea proprie a firmei.** Ruta nu cade pe global — o imagine
 * moștenită servită de pe adresa firmei ar arăta ca o ștampilă proprie. Previzualizarea
 * celei globale se face din `/setari`.
 */
export function companySettingFileUrl(companyId: string, setting: CompanySetting): string {
  const path = `/companies/${companyId}/settings/${encodeURIComponent(setting.name)}/file`;

  return `${apiFileUrl(path)}?v=${encodeURIComponent(setting.value ?? '')}`;
}
